import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  switchMap,
  catchError,
  of,
  startWith,
  map,
  shareReplay,
} from 'rxjs';

import { WorldNavCountsService, WorldNavCountsResponse } from '../../api/world-nav-counts.service';
import { WorldsService } from '../../api/worlds.service';
import { WorldNavComponent } from '../../shared/components/world-nav/world-nav.component';

@Component({
  selector: 'of-world-shell-page',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterModule, RouterLink, WorldNavComponent],
  template: `
    <ng-container *ngIf="vm$ | async as vm">
      <div class="world-shell" [class.world-shell--collapsed]="collapsed">

        <app-world-nav></app-world-nav>

        <div class="world-shell-content">
          <div class="breadcrumbs" *ngIf="vm.worldName">
            <ol>
              <li><a routerLink="/workspace">Workspace</a></li>
              <li><span aria-current="page">{{ vm.worldName }}</span></li>
            </ol>
          </div>
          <router-outlet></router-outlet>
        </div>
      </div>
    </ng-container>
  `,
})
export class WorldShellPage {
  worldId = '';
  collapsed = false;
  worldName: string | null = null;
  navId = `world-nav-${Math.random().toString(36).slice(2)}`;

  vm$: Observable<{ counts: WorldNavCountsResponse | null; worldName: string | null }>;
  private readonly countsRefresh$ = new BehaviorSubject<void>(undefined);

  constructor(
    route: ActivatedRoute,
    private readonly counts: WorldNavCountsService,
    private readonly worlds: WorldsService
  ) {
    this.worldId = route.snapshot.paramMap.get('worldId') ?? '';

    const counts$ = this.countsRefresh$.pipe(
      switchMap(() =>
        this.counts.get(this.worldId).pipe(
          catchError(() => of(null as WorldNavCountsResponse | null)),
          startWith(null as WorldNavCountsResponse | null)
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const worldName$ = this.worlds
      .get(this.worldId)
      .pipe(
        map((res) => res.world.name),
        catchError(() => of(null as string | null)),
        startWith(null as string | null),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.vm$ = combineLatest([counts$, worldName$]).pipe(
      map(([counts, name]) => {
        if (name) {
          this.worldName = name;
        }
        return { counts, worldName: name ?? this.worldName };
      })
    );
  }
}
